"use client";

import { useLocale } from "next-intl";
import * as React from "react";

import {
  useFundingEligibility,
  useFundingEligibilityIneligibleVisible,
  useFundingEligibilityStepOneGradient,
  useFundingEligibilitySuccessVisible,
} from "@/components/home/funding-eligibility-context";
import {
  computeFundingEligibilityEstimate,
  FUNDING_ELIGIBILITY_CROSSFADE,
  parseRevenue,
} from "@/components/home/funding-eligibility-shared";
import { FundingEligibilityStepCard } from "@/components/home/funding-eligibility-step-card";
import { Link } from "@/components/ui/link";
import { cn } from "@/lib/utils";

import { currencyCodeFor } from "./eligibility-currency";
import { FundingEligibilityCounterForm } from "./funding-eligibility-counter-form";
import type { FundingEligibilityFormCopy } from "./funding-eligibility-counter-form";

const GREY_500 = "text-[#7b8084]";

// Reveal for the estimate amount: a smooth, monotonic count-up from a lower
// figure to the real range (ease-out — fast start, gentle settle), rather
// than snapping straight to the final value.
const AMOUNT_REVEAL_DURATION_MS = 2200;

function easeOutCubic(t: number) {
  return 1 - (1 - t) ** 3;
}

function useRevealedEstimatedRange(
  estimatedRange: string,
  showAmount: boolean,
  copy: FundingEligibilityFormCopy
) {
  const locale = useLocale();
  const { formValues } = useFundingEligibility();
  const [displayed, setDisplayed] = React.useState(estimatedRange);
  const hasRevealedRef = React.useRef(false);
  const animationFrameRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!showAmount) {
      hasRevealedRef.current = false;
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    if (hasRevealedRef.current) {
      setDisplayed(estimatedRange);
      return;
    }

    hasRevealedRef.current = true;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplayed(estimatedRange);
      return;
    }

    const currencyCode = currencyCodeFor(formValues.country, locale);
    const targetRevenue = parseRevenue(formValues.revenue) ?? 0;
    const startTime = performance.now();

    function tick(now: number) {
      const progress = Math.min(
        (now - startTime) / AMOUNT_REVEAL_DURATION_MS,
        1
      );
      const risingRevenue = Math.round(targetRevenue * easeOutCubic(progress));
      setDisplayed(
        computeFundingEligibilityEstimate(
          String(risingRevenue),
          currencyCode,
          locale,
          formValues.country,
          copy.estimatedFundingUpToPrefix,
          formValues.category
        ) ?? ""
      );

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(tick);
      } else {
        animationFrameRef.current = null;
        setDisplayed(estimatedRange);
      }
    }

    animationFrameRef.current = requestAnimationFrame(tick);
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [
    showAmount,
    estimatedRange,
    locale,
    formValues.country,
    formValues.revenue,
    formValues.category,
    copy.estimatedFundingUpToPrefix,
  ]);

  return displayed;
}
const BODY =
  "text-[14px] leading-[1.5] tracking-[-0.28px] lg:text-[16px] lg:tracking-[-0.02em]";

export function FundingEligibilityCounterCard({
  copy,
  className,
  hideIntro = false,
  applyHref,
  applyLabel,
}: {
  copy: FundingEligibilityFormCopy;
  className?: string;
  /** Hide the "01. {step01Title}" heading + body — for pages with their own intro copy. */
  hideIntro?: boolean;
  /** When set, shows an apply CTA in the estimate card once the user is eligible. */
  applyHref?: string;
  applyLabel?: string;
}) {
  const {
    status,
    isEstimateLoading,
    ineligibleMessage,
    ineligibleRevenueDisplay,
  } = useFundingEligibility();
  const showSuccessUi = useFundingEligibilitySuccessVisible();
  const showIneligibleUi = useFundingEligibilityIneligibleVisible();
  const showStepOneGradient = useFundingEligibilityStepOneGradient();
  const showEstimateWatermark = status === "incomplete";
  const [estimatedRange, setEstimatedRange] = React.useState("");

  return (
    <FundingEligibilityStepCard
      showGradient={showStepOneGradient}
      className={className}
      contentClassName="flex min-h-0 flex-1 flex-col overflow-visible"
    >
      <div className="flex flex-1 flex-col gap-5 p-4 lg:gap-6 lg:p-6 xl:gap-6 xl:px-12 xl:pt-6 xl:pb-12">
        {hideIntro ? null : (
          <div className="flex max-w-none flex-col gap-4 xl:max-w-[320px]">
            <StepTitle index="01" title={copy.step01Title} />
            <p className={cn(BODY, GREY_500)}>{copy.step01Body}</p>
          </div>
        )}

        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:gap-[25px] xl:items-stretch xl:gap-6">
          <FundingEligibilityCounterForm
            copy={copy}
            className="w-full shrink-0 rotate-[0.12deg] lg:w-[210px]"
            onRangeChange={setEstimatedRange}
          />

          <EstimateCard
            copy={copy}
            estimatedRange={estimatedRange}
            isSuccess={showSuccessUi}
            isIneligible={showIneligibleUi}
            ineligibleMessage={ineligibleMessage}
            ineligibleRevenueDisplay={ineligibleRevenueDisplay}
            isLoading={isEstimateLoading}
            showWatermark={showEstimateWatermark}
            applyHref={applyHref}
            applyLabel={applyLabel}
            className="hidden min-w-0 flex-1 lg:flex"
          />
        </div>

        <MobileEstimateCard
          copy={copy}
          estimatedRange={estimatedRange}
          isSuccess={showSuccessUi}
          isIneligible={showIneligibleUi}
          ineligibleMessage={ineligibleMessage}
          ineligibleRevenueDisplay={ineligibleRevenueDisplay}
          isLoading={isEstimateLoading}
          showWatermark={showEstimateWatermark}
          applyHref={applyHref}
          applyLabel={applyLabel}
          className="lg:hidden"
        />
      </div>
    </FundingEligibilityStepCard>
  );
}

function StepTitle({ index, title }: { index: string; title: string }) {
  return (
    <p className="text-grey-950 text-[18px] leading-[1.3] tracking-[-0.36px] lg:text-[20px] lg:tracking-[-0.04em]">
      <span className={GREY_500}>{index}. </span>
      {title}
    </p>
  );
}

function EstimateCard({
  copy,
  className,
  estimatedRange,
  isSuccess,
  isIneligible,
  ineligibleMessage,
  ineligibleRevenueDisplay,
  isLoading,
  showWatermark,
  applyHref,
  applyLabel,
}: {
  copy: FundingEligibilityFormCopy;
  className?: string;
  estimatedRange: string;
  isSuccess: boolean;
  isIneligible: boolean;
  ineligibleMessage: string | null;
  ineligibleRevenueDisplay: string | null;
  isLoading: boolean;
  showWatermark: boolean;
  applyHref?: string;
  applyLabel?: string;
}) {
  return (
    <div
      className={cn(
        "relative flex min-h-[202px] w-full min-w-0 flex-col justify-between gap-4 overflow-hidden rounded-2xl bg-[#000633] p-[19px]",
        className
      )}
    >
      <EstimateGradient showWatermark={showWatermark} />

      <EstimateHeadline
        copy={copy}
        isSuccess={isSuccess}
        isIneligible={isIneligible}
        ineligibleMessage={ineligibleMessage}
      />

      <div className="flex items-end justify-between gap-4">
        <EstimateAmount
          copy={copy}
          estimatedRange={estimatedRange}
          isSuccess={isSuccess}
          isIneligible={isIneligible}
          ineligibleRevenueDisplay={ineligibleRevenueDisplay}
          isLoading={isLoading}
          size="desktop"
        />
        {isSuccess && applyHref ? (
          <ApplyButton href={applyHref} label={applyLabel} />
        ) : null}
      </div>
    </div>
  );
}

function ApplyButton({
  href,
  label,
  compact = false,
}: {
  href: string;
  label?: string;
  compact?: boolean;
}) {
  return (
    <Link
      href={href}
      external
      className={cn(
        // relative: EstimateGradient's decorative layers are absolutely
        // positioned — a non-positioned sibling paints *behind* them
        // regardless of DOM order, so this needs to be positioned too.
        "relative inline-flex shrink-0 items-center justify-center rounded-full bg-white font-medium whitespace-nowrap text-[#000633] transition-colors hover:bg-white/90",
        compact ? "h-8 px-3.5 text-[12px]" : "h-10 px-5 text-[14px]"
      )}
    >
      {label}
    </Link>
  );
}

function MobileEstimateCard({
  copy,
  estimatedRange,
  isSuccess,
  isIneligible,
  ineligibleMessage,
  ineligibleRevenueDisplay,
  isLoading,
  showWatermark,
  className,
  applyHref,
  applyLabel,
}: {
  copy: FundingEligibilityFormCopy;
  estimatedRange: string;
  isSuccess: boolean;
  isIneligible: boolean;
  ineligibleMessage: string | null;
  ineligibleRevenueDisplay: string | null;
  isLoading: boolean;
  showWatermark: boolean;
  className?: string;
  applyHref?: string;
  applyLabel?: string;
}) {
  return (
    <div
      className={cn("relative w-full overflow-visible rounded-2xl", className)}
    >
      <div
        className={cn(
          "relative flex min-h-[155px] w-full flex-col justify-between gap-4 overflow-hidden rounded-2xl bg-[#000633] p-[15px]",
          "border-grey-100 border"
        )}
      >
        <EstimateGradient compact showWatermark={showWatermark} />

        <EstimateHeadline
          copy={copy}
          isSuccess={isSuccess}
          isIneligible={isIneligible}
          ineligibleMessage={ineligibleMessage}
          compact
        />

        <div className="flex flex-wrap items-end justify-between gap-3">
          <EstimateAmount
            copy={copy}
            estimatedRange={estimatedRange}
            isSuccess={isSuccess}
            isIneligible={isIneligible}
            ineligibleRevenueDisplay={ineligibleRevenueDisplay}
            isLoading={isLoading}
            size="mobile"
          />
          {isSuccess && applyHref ? (
            <ApplyButton href={applyHref} label={applyLabel} compact />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function EstimateAmount({
  copy,
  estimatedRange,
  isSuccess,
  isIneligible,
  ineligibleRevenueDisplay,
  isLoading,
  size,
}: {
  copy: FundingEligibilityFormCopy;
  estimatedRange: string;
  isSuccess: boolean;
  isIneligible: boolean;
  ineligibleRevenueDisplay: string | null;
  isLoading: boolean;
  size: "desktop" | "mobile";
}) {
  const showAmount = isSuccess && !isLoading && estimatedRange.length > 0;
  const showIneligibleRevenue =
    isIneligible && Boolean(ineligibleRevenueDisplay);
  const isVisible = isLoading || isSuccess || showIneligibleRevenue;
  const revealedRange = useRevealedEstimatedRange(
    estimatedRange,
    showAmount,
    copy
  );

  if (!isVisible) {
    return null;
  }

  return (
    <div className="relative flex flex-col gap-1">
      <p className="text-[12px] leading-[1.4] text-white">
        {isSuccess
          ? copy.estimatedFundingSuccessLabel
          : showIneligibleRevenue
            ? copy.ineligibleRevenueLabel
            : copy.estimatedFundingLabel}
      </p>
      {isLoading ? (
        <div
          aria-busy="true"
          aria-label="Calculating estimated funding"
          className={cn(
            "flex items-center",
            size === "desktop" ? "min-h-[38px]" : "min-h-[29px]"
          )}
        >
          <span className="inline-flex gap-1">
            {[0, 1, 2].map((dot) => (
              <span
                key={dot}
                className="size-1.5 animate-pulse rounded-full bg-white/70"
                style={{ animationDelay: `${dot * 150}ms` }}
              />
            ))}
          </span>
        </div>
      ) : (
        <p
          className={cn(
            "text-white",
            size === "desktop"
              ? "min-h-[38px] [font-feature-settings:'ss01','ss02','ss03','zero'] text-[32px] leading-[1.2] tracking-[-0.04em]"
              : "min-h-[29px] text-[24px] leading-[1.2] tracking-[-0.96px]"
          )}
        >
          {showAmount ? revealedRange : ineligibleRevenueDisplay}
        </p>
      )}
    </div>
  );
}

function EstimateHeadline({
  copy,
  isSuccess,
  isIneligible,
  ineligibleMessage,
  compact = false,
}: {
  copy: FundingEligibilityFormCopy;
  isSuccess: boolean;
  isIneligible: boolean;
  ineligibleMessage: string | null;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        "relative flex min-w-0 items-start gap-4",
        compact ? "min-h-12" : "w-full max-w-full"
      )}
    >
      <div
        aria-hidden={isSuccess || isIneligible}
        className={cn(
          "flex w-full items-center gap-4",
          FUNDING_ELIGIBILITY_CROSSFADE,
          isSuccess || isIneligible
            ? "pointer-events-none opacity-0 blur-[2px]"
            : "opacity-100 blur-none"
        )}
      >
        <EstimateIcon compact={compact} />
        <p
          className={cn(
            "min-w-0 flex-1 text-balance text-white",
            compact
              ? "text-[16px] leading-[1.5] tracking-[-0.32px]"
              : "text-[20px] leading-[1.3] tracking-[-0.04em]"
          )}
        >
          {copy.estimatePrompt}
        </p>
      </div>
      <div
        aria-hidden={!isIneligible}
        className={cn(
          "absolute inset-0 flex items-start gap-4",
          FUNDING_ELIGIBILITY_CROSSFADE,
          isIneligible
            ? "opacity-100 blur-none"
            : "pointer-events-none opacity-0 blur-[2px]"
        )}
      >
        <IneligibleIcon compact={compact} />
        <p
          className={cn(
            "min-w-0 flex-1 text-balance text-white",
            compact
              ? "text-[16px] leading-[1.5] tracking-[-0.32px]"
              : "text-[20px] leading-[1.3] tracking-[-0.04em]"
          )}
        >
          {ineligibleMessage}
        </p>
      </div>
      <div
        aria-hidden={!isSuccess}
        className={cn(
          "absolute inset-0 flex items-start gap-4",
          FUNDING_ELIGIBILITY_CROSSFADE,
          isSuccess
            ? "opacity-100 blur-none"
            : "pointer-events-none opacity-0 blur-[2px]"
        )}
      >
        <SuccessIcon compact={compact} />
        <p
          className={cn(
            "min-w-0 flex-1 text-balance text-white",
            compact
              ? "text-[16px] leading-[1.3] tracking-[-0.32px]"
              : "text-[20px] leading-[1.3] tracking-[-0.04em]"
          )}
        >
          {copy.congratsTitle}
        </p>
      </div>
    </div>
  );
}

function SuccessIcon({ compact = false }: { compact?: boolean }) {
  return (
    <span className={cn("relative shrink-0", compact ? "size-12" : "size-16")}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/images/home/eligibility-success-icon.svg"
        alt=""
        className="absolute inset-0 size-full max-w-none"
      />
    </span>
  );
}

function IneligibleIcon({ compact = false }: { compact?: boolean }) {
  return (
    <span className={cn("relative shrink-0", compact ? "size-12" : "size-16")}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/images/home/eligibility-ineligible-icon.svg"
        alt=""
        className="absolute inset-0 size-full max-w-none"
      />
    </span>
  );
}

function EstimateGradient({
  compact = false,
  showWatermark,
}: {
  compact?: boolean;
  showWatermark: boolean;
}) {
  return (
    <>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[#000633]"
      />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/images/home/funding-estimate-gradient.png"
        alt=""
        aria-hidden
        className="pointer-events-none absolute inset-0 size-full object-cover"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40 mix-blend-overlay"
        style={{
          backgroundImage: "url(/images/noise-texture.png)",
          backgroundSize: "cover",
        }}
      />
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute right-0 bottom-0 mix-blend-overlay",
          FUNDING_ELIGIBILITY_CROSSFADE,
          showWatermark ? "opacity-100" : "opacity-0"
        )}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/images/home/eligibility-estimate-watermark.svg"
          alt=""
          className={cn(
            "max-w-none",
            compact ? "h-[110px] w-[150px]" : "h-[147px] w-[200px]"
          )}
        />
      </span>
    </>
  );
}

function EstimateIcon({ compact = false }: { compact?: boolean }) {
  return (
    <span className={cn("relative shrink-0", compact ? "size-12" : "size-16")}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/images/home/eligibility-estimate-icon.svg"
        alt=""
        className="absolute inset-0 size-full max-w-none"
      />
    </span>
  );
}
